import {
  Button,
  Col,
  DatePicker,
  Empty,
  Form,
  Image,
  Input,
  Row,
  Select,
  Spin,
  message,
  Space,
  Popover,
} from "antd";
import locale from "antd/es/date-picker/locale/es_ES";
import { DocumentCard, PhoneInput } from "cmt-ui-kit";
import moment from "moment";
import "moment/locale/es";
import { Fragment, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "react-phone-input-2/lib/style.css";
import {
  admissionService,
  getServicesSuggestions,
} from "../../../services/admission";

import { getCountries, getGenders, getCommunes } from "../../../services/general";
import { getPrestationByAdmissionPatient } from "../../../services/orderDetails";
import WebcamModal from "../../components/WebcamModal";
import usePatientPhoto from "../../components/hooks/usePatientPhoto";
import PatientPhotoButtons from "../../components/PatientPhotoButtons";
import countrySettings from "../countrySettings.json";

import evaluationTypes from '../../../utils/evaluationType.json';
import { isProfessionRequiredByEvaluationType } from "../../../utils/evaluationRules";

const { Option } = Select;
const { format, clean, validate } = require("rut.js");

const PatientFormCL = ({
  form,
  reservation,
  patientToAdmission,
  setPatientToAdmission,
  setCurrentStep,
  providerCode,
}) => {
  const countryCode = 'CL';

  const [loading, setLoading] = useState({
    patient: false,
    servicesByPatient: false,
    nextStep: false,
    gender: false,
    countries: false,
  });
  const [genders, setGenders] = useState();
  const [countries, setCountries] = useState();
  const [communes, setCommunes] = useState();
  const [documentType, setDocumentType] = useState('rut');
  const [showMessage, setShowMessage] = useState({ datePicker: false });
  const [disabled, setDisabled] = useState({});
  const [isProfessionRequired, setIsProfessionRequired] = useState(true);
  const [isProfessionEditable, setIsProfessionEditable] = useState(true);
  const { t } = useTranslation();

  const patientPhoto = usePatientPhoto({
    form,
    patientToAdmission,
    setLoading,
    countryCode,
  });

  const fetchGenders = useCallback(async () => {
    setLoading(l => ({ ...l, gender: true }));
    try {
      const response = await getGenders();
      setGenders(response);
    } catch (error) {
      console.error(error);
      message.error({ content: "Error en fetch de géneros", style: { marginTop: "45vh" } });
    }
    setLoading(l => ({ ...l, gender: false }));
  }, []);

  const fetchCountries = useCallback(async () => {
    setLoading(l => ({ ...l, countries: true }));
    try {
      const response = await getCountries();
      setCountries(response);
    } catch (error) {
      console.error(error);
      message.error({ content: "Error en fetch de países", style: { marginTop: "45vh" } });
    }
    setLoading(l => ({ ...l, countries: false }));
  }, []);

  const fetchCommunes = async () => {
    try {
      const response = await getCommunes({ country_code: countryCode });
      setCommunes(response);
    } catch (error) {
      console.error(error);
      message.error({ content: "Error al buscar comunas", style: { marginTop: "45vh" } });
    }
  };

  const confirmRefreshPage = (event) => {
    event.returnValue = '';
    return '';
  };

  useEffect(() => {
    try {
      if (patientToAdmission) {
        form.setFieldsValue({
          id: patientToAdmission["id"],
          documentType: patientToAdmission["documentType"] || 'rut',
          documentNumber:
            patientToAdmission["documentType"] === "rut"
              ? format(patientToAdmission["documentNumber"])
              : patientToAdmission["documentNumber"],
          countryCode:
            patientToAdmission["documentType"] === "passport" &&
            patientToAdmission["countryCode"],
          genderId: patientToAdmission["genderId"],
          email: patientToAdmission["email"],
          userFirstName: patientToAdmission["userFirstName"],
          userLastName: patientToAdmission["userLastName"],
          dateBirth: patientToAdmission.dateBirth
            ? moment(patientToAdmission["dateBirth"], "YYYY-MM-DD")
            : moment(),
          phone: PhoneInput.formatPhone(
            patientToAdmission["phone"],
            countryCode
          ),
          profession:
            patientToAdmission["profession"] &&
            patientToAdmission["profession"],
          commune_id: patientToAdmission["commune_id"],
          address: patientToAdmission["address"],
        });
        setDocumentType(patientToAdmission["documentType"] || 'rut');
        const requiredByEvaluationType = isProfessionRequiredByEvaluationType(
          patientToAdmission?.evaluation_type,
          countryCode,
          evaluationTypes,
        );
        setIsProfessionRequired(requiredByEvaluationType);
        setIsProfessionEditable(requiredByEvaluationType);
      } else {
        form.setFieldsValue({
          documentType: documentType,
          patientToAdmission: moment(),
        });
      }

      fetchGenders();
      fetchCommunes();
      window.addEventListener("beforeunload", confirmRefreshPage);

      return () => {
        window.removeEventListener("beforeunload", confirmRefreshPage);
      };
    } catch (error) {
      console.error(error);
      message.error({
        content: `Error interno ${error}`,
        style: { marginTop: "45vh" },
      });
    }
  }, []);

  useEffect(() => {
    if (documentType === "passport") {
      fetchCountries();
    }
  }, [patientToAdmission, fetchCountries]);

  const handleOnBlur = async (event) => {
    let inputValue = event.target.value;
    let result;
    let admissionPatientId;
    let previousExams = [];
    let evaluation_type;
    let profession;
    try {
      if (inputValue) {
        setLoading((loading) => {
          return { ...loading, patient: true };
        });
        if (documentType === "rut") {
          const validateRut = validate(inputValue);
          if (validateRut) {
            result = await fetchPatient(
              "rut",
              format(inputValue, { dots: false })
            );
          }
        } else {
          result = await fetchPatient(documentType, inputValue);
        }
        if (result?.length > 0 && result[0]) {
          const searchPatient = result[0];
          patientPhoto.fetchPatientIdCard({ id: searchPatient.id });
          profession = searchPatient.position;

          setLoading((loading) => {
            return { ...loading, servicesByPatient: true };
          });
          const [prestationResponse, suggestions] = await Promise.all([
            getPrestationByAdmissionPatient({
              reservation_id: reservation.id,
              patient_id: searchPatient.id,
              provider_code: providerCode,
            }),
            fetchSuggestions(searchPatient.id),
          ]);

          let cleanedSuggestions = [];
          previousExams = prestationResponse ? prestationResponse : [];
          if (prestationResponse?.length > 0) {
            admissionPatientId = prestationResponse[0].admission_patient_id;
          }

          let requiredByEvaluationType = true;
          if (suggestions && suggestions.length > 0) {
            evaluation_type = suggestions[0].evaluation_type;
            profession = suggestions[0].profession;
            requiredByEvaluationType = isProfessionRequiredByEvaluationType(evaluation_type, countryCode, evaluationTypes);
            setIsProfessionRequired(requiredByEvaluationType);
            setIsProfessionEditable(requiredByEvaluationType);
            profession = requiredByEvaluationType ? profession : null;
            cleanedSuggestions = suggestions.map(({ evaluation_type, profession, ...rest }) => rest);
          }

          form.setFieldsValue({
            id: searchPatient.id,
            documentType: searchPatient.document_type,
            documentNumber:
              documentType === "rut"
                ? format(searchPatient.document_number)
                : searchPatient.document_number,
            ...(documentType === "passport" && {
              countryCode: searchPatient.country,
            }),
            userFirstName: searchPatient.names,
            userLastName: searchPatient.last_name,
            email: searchPatient.email,
            genderId:
              searchPatient.gender !== null
                ? parseInt(searchPatient.gender)
                : null,
            dateBirth:
              searchPatient.birthdate &&
              moment(searchPatient.birthdate, "YYYY-MM-DD"),
            phone: PhoneInput.formatPhone(searchPatient.phone, countryCode),
            profession: profession,
            address: searchPatient.address,
            commune_id: searchPatient.commune_id
          });

          setPatientToAdmission((patientToAdmission) => {
            return {
              ...patientToAdmission,
              preadmissionPrestations: cleanedSuggestions,
              previousPrestations: previousExams,
              admissionPatientId: admissionPatientId,
              prestations: previousExams.length > 0 ? [] : cleanedSuggestions,
              evaluation_type,
              profession,
              originalProfession: searchPatient.position,
              hasPrefilledEvaluationType: evaluation_type != null,
              consents: null,
              laboratoryPrestations: [],
              diabetic: null,
            };
          });
          setLoading((loading) => {
            return { ...loading, servicesByPatient: false };
          });
        } else {
          form.resetFields();
          form.setFieldsValue({
            documentType: documentType,
            documentNumber:
              documentType === "rut" ? format(inputValue) : inputValue,
          });

          patientPhoto.resetImage();

          setPatientToAdmission((patientToAdmission) => {
            return {
              ...patientToAdmission,
              preadmissionPrestations: [],
              previousPrestations: [],
              admissionPatientId: null,
              prestations: [],
              evaluation_type: null,
              consents: null,
              laboratoryPrestations: [],
              diabetic: null,
              originalProfession: null,
            };
          });
        }
      }
    } catch (error) {
      console.error(error);
      message.error({
        content: `Error interno ${error}`,
        style: { marginTop: "45vh" },
      });
    } finally {
      setLoading((loading) => {
        return { ...loading, patient: false };
      });
    }
  };

  const fetchPatient = async (documentType, documentNumber) => {
    setLoading((loading) => {
      return { ...loading, patient: true };
    });
    let response;
    let isDisabled = false;
    try {
      response = await admissionService.getPatient({
        document_type: documentType,
        document_number: documentNumber,
      });
    } catch (error) {
      console.error(error);
      let errorMessage = 'Error al buscar al paciente';
      if (error === 'blocked') {
        errorMessage = `No es posible admisionar al paciente ${documentNumber}.`;
        form.resetFields();
        form.setFieldsValue({
          documentType: documentType,
          documentNumber:
            documentType === "rut" ? format(documentNumber) : documentNumber,
        });
        isDisabled = true;
      }
      message.error({
        content: errorMessage,
        style: {
          marginTop: '30vh',
          fontSize: 16
        }
      });
    } finally {
      setLoading((loading) => {
        return { ...loading, patient: false };
      });
      setDisabled({ blockingPatient: isDisabled });
      return response;
    }
  };

  const fetchSuggestions = async (patientId) => {
    try {
      const response = await getServicesSuggestions({
        reservationId: reservation.id,
        patientId,
        providerCode,
      });
      return response;
    } catch (error) {
      message.error({
        content: `Error al traer las sugerencias`,
        style: { marginTop: "45vh" },
      });
      console.error(error);
    }
  };

  const handleNextStep = async () => {
    setLoading((loading) => {
      return { ...loading, nextStep: true };
    });
    try {
      form
        .validateFields()
        .then((value) => {
          let _patient = form.getFieldsValue();
          for (const key in _patient) {
            if (key === "id" && !_patient["id"]) {
              _patient = { ..._patient, id: null };
            }

            if (key === "documentType" && _patient["documentType"] === "rut") {
              const splitRut = _patient["documentNumber"].split("-");
              const cleanRut = clean(splitRut[0]);
              _patient = {
                ..._patient,
                documentNumber: `${cleanRut}-${splitRut[1]}`,
                countryCode: countryCode,
              };
            }
            if (key === "dateBirth") {
              _patient = {
                ..._patient,
                dateBirth: moment(_patient["dateBirth"], "YYYY-MM-DD").format(
                  "YYYY-MM-DD"
                ),
              };
            }

            if (key === "phone") {
              if (
                _patient["phone"] === "" ||
                !_patient["phone"] ||
                _patient["phone"].length <= 4
              ) {
                _patient = { ..._patient, phone: null };
              } else if (_patient["phone"].startsWith("+")) {
                _patient = { ..._patient, phone: _patient["phone"] };
              } else {
                _patient = { ..._patient, phone: `+${_patient["phone"]}` };
              }
            }

            if (
              key === "profession" &&
              (_patient["profession"] === "" || !_patient["profession"])
            ) {
              _patient = { ..._patient, profession: null };
            }

            Object.keys(_patient).forEach((key) => {
              if (_patient[key] === undefined) {
                delete _patient[key];
              }
            });

            setLoading((loading) => {
              return { ...loading, nextStep: false };
            });
          }
          setPatientToAdmission({ ...patientToAdmission, ..._patient });
          setLoading((loading) => {
            return { ...loading, nextStep: false };
          });
          setCurrentStep(1);
        })
        .catch((errorInfo) => {
          if (errorInfo.errorFields.length > 0) {
            return;
          }
        })
        .finally(() => {
          setLoading((loading) => {
            return { ...loading, nextStep: false };
          });
        });
    } catch (error) {
      console.error(error);
      message.error({
        content: `Error interno ${error}`,
        style: { marginTop: "45vh" },
      });
    }
  };

  const disabledDate = (current) => {
    try {
      const overAge = moment().subtract(8, "years");
      return current && overAge < current;
    } catch (error) {
      console.error(error);
      message.error({
        content: `Error interno ${error}`,
        style: { marginTop: "45vh" },
      });
    }
  };

  const validateDate = (value, text) => {
    const age = moment().diff(moment(text, "DD-MM-YYYY"), "years");
    setShowMessage((messages) => ({ ...messages, datePicker: age < 18 }));
  };

  return (
    <Spin className="cmt-spin" spinning={loading.patient || loading.servicesByPatient}>
      <Row justify="center" className="admission-settings__patient-form mt-50">
        <Fragment>
          <Col lg={15} xs={22}>
            <Form form={form} size="large" layout="vertical">
              <Form.Item name="id" hidden>
                <Input type="hidden" />
              </Form.Item>
              <Form.Item name="prestations" hidden>
                <Select type="hidden" />
              </Form.Item>
              <Form.Item name="suggestions" hidden>
                <Select type="hidden" />
              </Form.Item>
              <Row justify="space-between" align="bottom" gutter={16}>
                <Col lg={12} xs={24} className="mb-20 mt-60">
                  <h2 className="mb-20">
                    {t("admissionPage.patientForm.patientDataTitle")}
                  </h2>
                  <Row>
                    <p className="text-label mb-10">
                      Tipo y número de documento
                    </p>
                  </Row>
                  <DocumentCard
                    countryCode={countryCode}
                    setDocumentType={setDocumentType}
                    form={form}
                    handleOnDocumentNumberBlur={handleOnBlur}
                    type="person"
                  />
                </Col>
                {documentType === "passport" && (
                  <Col lg={12} xs={24}>
                    {loading.countries ? (
                      <Row justify="center">
                        <Spin className="custom-spinner" />
                      </Row>
                    ) : (
                      countries && (
                        <Form.Item
                          name="countryCode"
                          label="País"
                          className="mb-20"
                          rules={[
                            {
                              required: true,
                              message: "País es requerido",
                            },
                          ]}
                        >
                          <Select
                            id="countryCode"
                            className="cmt-select"
                            placeholder="Seleccione país"
                            filterOption={(input, option) =>
                              option.children
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            showSearch
                          >
                            {countries.map((country, index) => {
                              return (
                                <Option key={index} value={country.code}>
                                  {country.name}
                                </Option>
                              );
                            })}
                          </Select>
                        </Form.Item>
                      )
                    )}
                  </Col>
                )}
              </Row>
              <Row justify="space-between" gutter={16}>
                <Col lg={12} xs={24}>
                  <Form.Item
                    name="userFirstName"
                    label="Nombres"
                    rules={[
                      {
                        required: true,
                        message: "Nombres son requeridos",
                      },
                    ]}
                  >
                    <Input
                      id="userFirstName"
                      className="cmt-input"
                      placeholder="Ingrese nombres"
                    />
                  </Form.Item>
                </Col>
                <Col lg={12} xs={24}>
                  <Form.Item
                    name="userLastName"
                    label="Apellidos"
                    rules={[
                      {
                        required: true,
                        message: "Apellidos son requeridos",
                      },
                    ]}
                  >
                    <Input
                      id="userLastName"
                      className="cmt-input"
                      placeholder="Ingrese apellidos"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row justify="space-between" gutter={16}>
                <Col lg={12} xs={24}>
                  {loading.gender ? (
                    <Row justify="center">
                      <Spin className="custom-spinner" />
                    </Row>
                  ) : (
                    genders && (
                      <Form.Item
                        name="genderId"
                        label="Sexo"
                        rules={[
                          {
                            required: true,
                            message: "Sexo es requerido",
                          },
                        ]}
                      >
                        <Select
                          id="genderId"
                          className="cmt-select"
                          placeholder="Seleccione sexo"
                        >
                          {genders.map((gender, index) => {
                            return (
                              <Option key={index} value={gender.id}>
                                {gender.name}
                              </Option>
                            );
                          })}
                        </Select>
                      </Form.Item>
                    )
                  )}
                </Col>
                <Col lg={12} xs={24}>
                  <Form.Item
                    name="dateBirth"
                    label="Fecha de nacimiento"
                    rules={[
                      {
                        required: true,
                        message: "Fecha de nacimiento es requerida",
                      },
                    ]}
                    extra={
                      showMessage.datePicker && (
                        <span className="underAgeAlert">
                          Estas admisionando un paciente{" "}
                          <strong>menor de edad</strong>.
                        </span>
                      )
                    }
                  >
                    <DatePicker
                      id="dateBirth"
                      className="date-picker"
                      locale={locale}
                      placeholder="Seleccione fecha de nacimiento"
                      popupClassName="custom-dropdown"
                      style={{ width: "100%" }}
                      format="DD-MM-YYYY"
                      disabledDate={disabledDate}
                      onChange={validateDate}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row justify="space-between" gutter={16}>
                <Col lg={12} xs={24}>
                  <PhoneInput.ReactPhone
                    countryCode={countryCode}
                    form={form}
                    labelText={t("admissionPage.patientForm.phonePlaceholder")}
                    optional={true}
                  />
                </Col>
                <Col lg={12} xs={24}>
                  <Form.Item
                    name="profession"
                    rules={[
                      {
                        required: isProfessionRequired,
                        whitespace: true,
                        message: "Cargo es requerido",
                      },
                    ]}
                    label={
                      <Space size={4}>
                        {t('admissionPage.patientForm.professionPlaceholder')}
                        {!isProfessionEditable && (
                          <Popover
                            overlayClassName="profession-admission-popover"
                            content={
                              <p className="mb-0">
                                {t('admissionPage.patientForm.professionPopoverDisabledText')}
                              </p>
                            }
                            placement="right"
                            trigger="hover"
                          >
                            <span className="icon-alert-rounded info-icon" />
                          </Popover>
                        )}
                      </Space>
                    }
                  >
                    <Input
                      id="profession"
                      className="cmt-input"
                      placeholder={isProfessionEditable ? "Ingrese cargo" : ""}
                      disabled={!isProfessionEditable}
                    />
                  </Form.Item>
                </Col>
                <Col lg={12} xs={24}>
                  <Form.Item
                    name="email"
                    label={t("admissionPage.patientForm.emailPlaceholder")}
                    rules={[
                      {
                        required: true,
                        message: "correo electrónico es requerido",
                      },
                      {
                        type: "email",
                        message: "Correo electrónico inválido",
                      },
                    ]}
                  >
                    <Input
                      id="email"
                      type="email"
                      className="cmt-input"
                      placeholder="Ingrese correo electrónico"
                    />
                  </Form.Item>
                </Col>
                <Col lg={12} xs={24}>
                    <Form.Item label={t("admissionPage.patientForm.addressPlaceholder")} style={{ width: '100%' }}>
                      <Input.Group compact>
                        <Form.Item
                          id="commune_id"
                          name="commune_id"
                          style={{ width: '40%' }}
                          rules={[{ required: true, message: `${t('reservations.companyModal.communeLabel')} es requerida` }]}
                        >
                          <Select
                            showSearch
                            filterOption={(input, option) =>
                              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            placeholder={t("admissionPage.patientForm.communePlaceholder")}
                            options={communes?.map((commune) => ({ label: `${commune.name} - ${commune.province}`, value: commune.id }))}
                          />
                        </Form.Item>
                        <Form.Item
                          name="address"
                          style={{ width: '60%' }}
                          rules={[
                            {
                              required: true,
                              message: "la direccion es requerida",
                            },
                          ]}
                        >
                          <Input
                            id="address"
                            type="input"
                            className="cmt-input"
                            placeholder="Ingrese dirección"
                          />
                        </Form.Item>
                      </Input.Group>
                    </Form.Item>
                  </Col>
                <Col lg={14} xs={24}>
                  <h2 className="mb-20 mt-100">
                    {t("admissionPage.patientForm.patientPhotoTitle")}
                  </h2>
                  <PatientPhotoButtons patientPhoto={patientPhoto}>
                    <Form.Item name="pictureIdCard">
                      <Row>
                        <Col lg={12} xs={24}>
                          {patientPhoto.imageSrc && !patientPhoto.imageSrc.includes("missing") ? (
                            <Image
                              src={patientPhoto.imageSrc}
                              alt="patient"
                              style={{ width: 276, height: 284 }}
                              className="mb-20"
                            />
                          ) : (
                            <Empty
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                              description="Sin foto"
                              className="mb-20"
                            />
                          )}
                          <Input type="hidden" />
                        </Col>
                      </Row>
                    </Form.Item>
                  </PatientPhotoButtons>
                </Col>
              </Row>
            </Form>
          </Col>
          <Col lg={15} xs={22} className="mt-50 mb-60">
            <Row justify="end">
              <Col xl={3} lg={4} xs={24}>
                <Button
                  loading={loading.nextStep || loading.servicesByPatient}
                  onClick={handleNextStep}
                  className={`cmt-button primary ${disabled?.blockingPatient ? 'disabled' : ''}`}
                  type="primary"
                  size="large"
                  disabled={disabled?.blockingPatient}
                >
                  Siguiente
                </Button>
              </Col>
            </Row>
          </Col>
        </Fragment>
      </Row>
      <WebcamModal
        {...patientPhoto}
        countrySettings={countrySettings}
        countryCode={countryCode}
      />
    </Spin>
  );
};

export default PatientFormCL;
